import { useState } from "react"
import useCoverPreview from "../hooks/useCoverPreview"
import useBookForm from "../hooks/useBookForm"

// Ukrainian labels for the fields, used for both the floating labels and for
// dressing up any server-side error keys that slip through client validation.
const LABELS = { title: "Назва", author: "Автор", genre_id: "Жанр" }

// A single text field with a floating label. The label rests inside the input
// and lifts up on focus or once there's a value — a small, cozy micro-interaction.
const Field = ({ name, value, error, onChange, autoFocus, delay }) => {
  const [focused, setFocused] = useState(false)
  const active = focused || Boolean(value)

  return (
    <div
      className={`cf-field${active ? " is-active" : ""}${error ? " is-error" : ""}`}
      style={{ animationDelay: `${delay}ms` }}
    >
      <input
        id={`book_${name}`}
        type="text"
        value={value}
        autoFocus={autoFocus}
        onChange={(e) => onChange(name, e.target.value)}
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
        autoComplete="off"
      />
      <label htmlFor={`book_${name}`}>{LABELS[name]}</label>
      <span className="cf-underline" />
      <div className="cf-error" aria-live="polite">{error}</div>
    </div>
  )
}

// The whole create/edit form. Submits over fetch as multipart FormData so we can
// stay on the page and play submit → success animations instead of a full reload.
const BookForm = ({ config }) => {
  const { preview, coverName, fileRef, onFile } = useCoverPreview(config.cover_url)
  const { values, errors, status, formError, shake, change, submit } =
    useBookForm(config, () => fileRef.current?.files[0])

  const cancelHref = "/books"

  return (
    <form
      className={`cozy-form${shake ? " is-shaking" : ""}${status === "success" ? " is-done" : ""}`}
      onSubmit={submit}
      noValidate
    >
      {formError && <div className="cf-form-error">{formError}</div>}

      <Field name="title" value={values.title} error={errors.title} onChange={change} autoFocus delay={60} />
      <Field name="author" value={values.author} error={errors.author} onChange={change} delay={120} />

      <div
        className={`cf-genres${errors.genre_id ? " is-error" : ""}`}
        style={{ animationDelay: "180ms" }}
      >
        <span className="cf-genres__label">{LABELS.genre_id}</span>
        <div className="cf-genres__chips" role="radiogroup" aria-label={LABELS.genre_id}>
          {config.genres.map((g) => {
            const selected = String(g.id) === values.genre_id
            return (
              <button
                type="button"
                key={g.id}
                role="radio"
                aria-checked={selected}
                className={`cf-chip${selected ? " is-selected" : ""}`}
                onClick={() => change("genre_id", String(g.id))}
              >
                {g.name}
              </button>
            )
          })}
        </div>
        <div className="cf-error" aria-live="polite">{errors.genre_id}</div>
      </div>

      <div className="cf-cover" style={{ animationDelay: "240ms" }}>
        <span className="cf-cover__label">Обкладинка</span>
        <label className="cf-cover__drop" htmlFor="book_cover">
          {preview ? (
            <img key={preview} className="cf-cover__preview" src={preview} alt="" />
          ) : (
            <span className="cf-cover__icon" aria-hidden="true">＋</span>
          )}
          <span className="cf-cover__hint">
            {coverName ? coverName : preview ? "Натисни, щоб замінити" : "Обери зображення"}
          </span>
        </label>
        <input
          ref={fileRef}
          id="book_cover"
          type="file"
          accept="image/*"
          onChange={onFile}
          hidden
        />
      </div>

      <div className="cf-actions" style={{ animationDelay: "300ms" }}>
        <button type="submit" className={`btn btn-primary cf-submit cf-submit--${status}`} disabled={status !== "idle"}>
          <span className="cf-submit__label">
            {config.persisted ? "Зберегти зміни" : "Додати на полицю"}
          </span>
          <span className="cf-submit__spinner" aria-hidden="true" />
          <span className="cf-submit__check" aria-hidden="true">✓</span>
        </button>
        <a className="btn btn-ghost" href={cancelHref}>Скасувати</a>
      </div>
    </form>
  )
}

export default BookForm
